import * as React from 'react';
import Highcharts from 'highcharts';
import { useComponentsContext } from '@oms/components-config-context';
import {
  InstrumentChartFetcher,
  Chart,
  buildOptions,
  SELECTION,
  SELECTION_ONE_MONTH,
  COLORS,
  UseInstrumentChartReturn,
} from '@oms/components-instrument-chart';

import { Legend } from './Legend';
import { Filters } from './Filters';
import { TI_COLORS } from './constants';

export interface AnalysisChartProps {
  /** The main series of the chart */
  itemSector: string;
  /** The className to pass to the component */
  className?: string;
  /** A className to apply to the expand button */
  expandButtonClassName?: string;
  /** The initial period to display in the chart */
  initialSelection?: SELECTION;
  /**
   * When set to true, the filters will be rendered inside an expandable
   * collapsed element
   */
  collapsableFilters?: boolean;
  /**
   * A list of CSS color strings that will be used as the stroke color on the
   * series in the chart. When not provided, a default defined in constants.js
   * will be used.
   *
   * When all colors in the set have been used, it wil loop around and use
   * colors from the start of the set.
   */
  seriesColors?: string[];
  /**
   * A list of CSS color strings that will be used as the stroke color on the
   * technical indicators in the chart. When not provided, a default defined in
   * constants.js will be used.
   *
   * When all colors in the set have been used, it wil loop around and use
   * colors from the start of the set.
   */
  indicatorsColors?: string[];
  /**
   * Any overrides to the highcharts config object. Will be merged into the
   * default configuration object provided with the component. See
   * https://api.highcharts.com/highstock/ for configuration options.
   */
  options?: Record<string, any>;
  /**
   * The group to find matches in when searching for comparison instruments.
   * This group needs to be pre-defined by OMS in the server used to fetch data
   * from.
   */
  searchGroup?: string;
  /**
   * The `onError` prop provides a way to override the default `<ErrorBoundary>`
   * behavior for this component and render a different fallback UI when the
   * component throws an error.
   *
   * @param {Object} errorDescriptor - An object containing details of the error
   * @param {Error} errorDescriptor.error - The error that was thrown
   * @param {Function} errorDescriptor.reset - Resets the error and attempts
   * another render
   * @returns {React.Component} - The fallback UI that is rendered on an error
   */
  onError?: ({
    error,
    reset,
  }: {
    error: Error;
    reset: () => void;
  }) => React.ReactNode;
  /** A spec to override the default values of the news query */
  newsSpec?: Record<string, any>;
  /** A spec to override the default values of the dividends query */
  dividendsSpec?: Record<string, any>;
  /**
   * Enables functionality that is only useful for logged-in users like
   * transactions on the given instrument.
   */
  isLoggedIn?: boolean;
  /**
   * Arbitrary parameters that are passed to the URL template engine and
   * inserted into the `transactionsUrl` template from the context.
   */
  transactionsParams?: Record<string, any>;
}

const overrides = {
  navigator: {
    enabled: true,
  },
};

/**
 * A chart that provides options for analyzing the historical share prices of an
 * instrument in order to forecast the direction of the price in the future.
 *
 * Various indicators are available for analyzing the stock. Additions like news
 * and dividends, comparison with other instruments and technical analyses like
 * SMA over a given period.
 *
 * `AnalysisDataFetcher` does not use `JawsProvider` and is configured via
 * `ComponentsContext`.
 *
 * @since 1.0.0
 */
export const AnalysisChart = (props: AnalysisChartProps) => {
  const {
    itemSector,
    onError,
    className = 'AnalysisChart',
    expandButtonClassName = 'AnalysisChart--expandButton',
    collapsableFilters = false,
    isLoggedIn = false,
    seriesColors = COLORS,
    indicatorsColors = TI_COLORS,
    options = {},
    searchGroup = 'SEARCH',
    transactionsParams = {},
    newsSpec = {},
    dividendsSpec = {},
    initialSelection = SELECTION_ONE_MONTH,
  } = props;

  const { ErrorBoundary } = useComponentsContext();
  const [selection, setSelection] = React.useState<SELECTION>(initialSelection);

  return (
    <ErrorBoundary component="AnalysisChart" onError={onError}>
      <InstrumentChartFetcher
        selection={selection}
        itemSector={itemSector}
        transactionsParams={transactionsParams}
        newsSpec={newsSpec}
        dividendsSpec={dividendsSpec}
      >
        {({
          main,
          breaks,
          compares,
          additions,
          technicalIndicators,
          addCompare,
          addTechnicalIndicator,
          activateAddition,
          removeCompare,
          removeTechnicalIndicator,
          removeAddition,
          loading,
        }: UseInstrumentChartReturn) => {
          if (!main || !main.instrument) return null;
          const defaultOptions = buildOptions({
            main,
            selection,
            compares,
            additions,
            technicalIndicators,
            colors: seriesColors,
            indicatorsColors,
            breaks,
          });

          return (
            <div className={className}>
              <Legend
                instrument={main.instrument}
                seriesColors={seriesColors}
                indicatorsColors={indicatorsColors}
                compares={compares}
                technicalIndicators={technicalIndicators}
                additions={additions}
                removeCompare={removeCompare}
                removeTechnicalIndicator={removeTechnicalIndicator}
                removeAddition={removeAddition}
              />
              <Chart
                options={Highcharts.merge(defaultOptions, overrides, options)}
                loading={loading}
              />
              <Filters
                collapsableFilters={collapsableFilters}
                expandButtonClassName={expandButtonClassName}
                isLoggedIn={isLoggedIn}
                addCompare={addCompare}
                setSelection={setSelection}
                selection={selection}
                additions={additions}
                addAddition={activateAddition}
                removeAddition={removeAddition}
                addTechnicalIndicator={addTechnicalIndicator}
                searchGroup={searchGroup}
              />
            </div>
          );
        }}
      </InstrumentChartFetcher>
    </ErrorBoundary>
  );
};
